// services/aiService.js
import axios from 'axios';
import { getFirebaseAuthToken } from '../../utils/firebaseAuthToken';
import {
  AI_PROVIDERS,
  getProviderForModel,
  API_KEYS
} from '../constants/aiConstants';
import {
  TRANSLATION_INSTRUCTIONS,
  DEFAULT_TRANSLATION_MODEL,
  getModelSettings,
  validateTranslation,
  getOptimalTokenLimit,
  sanitizeTranslationOutput,
  sanitizeTranslatedJson
} from '../constants/translationConfig';

const hasTranslationContent = (value) => {
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return Boolean(value && typeof value === 'object' && Object.keys(value).length > 0);
};

const selectTranslationFields = (source, fieldNames) => {
  if (!source || typeof source !== 'object') return {};

  return fieldNames.reduce((selected, fieldName) => {
    if (hasTranslationContent(source[fieldName])) {
      selected[fieldName] = source[fieldName];
    }
    return selected;
  }, {});
};

const buildFeedbackTranslationPayload = (feedback) => {
  if (!feedback || typeof feedback !== 'object') return undefined;

  const selectQuestionText = (question) => {
    if (!question || typeof question !== 'object') return undefined;

    const selected = selectTranslationFields(question, ['title', 'prompt']);
    if (Array.isArray(question.options)) {
      selected.options = question.options.map(option => selectTranslationFields(option, ['label']));
    }
    return selected;
  };

  const selected = {};
  if (Array.isArray(feedback.micro_prompts)) {
    selected.micro_prompts = feedback.micro_prompts.map(selectQuestionText);
  }
  if (feedback.issue_question) {
    selected.issue_question = selectQuestionText(feedback.issue_question);
  }
  return hasTranslationContent(selected) ? selected : undefined;
};

const buildMetadataTranslationPayload = (metadata = {}, title = '', description = '') => {
  const selected = selectTranslationFields(metadata, [
    'name',
    'description',
    'subtitle',
    'theme',
    'targetAudience',
    'categories',
    'tags'
  ]);

  if (!selected.name && title) selected.name = title;
  if (!selected.description && description) selected.description = description;

  const museumInfo = selectTranslationFields(metadata.museum_info, [
    'venue_type',
    'description',
    'opening_hours',
    'admission',
    'accessibility',
    'wifi',
    'gift_shop',
    'cafe'
  ]);
  if (hasTranslationContent(museumInfo)) selected.museum_info = museumInfo;

  const feedback = buildFeedbackTranslationPayload(metadata.feedback);
  if (feedback) selected.feedback = feedback;

  return selected;
};

const buildPoiTranslationPayload = (poi = {}, includeNarration = true, includeSummary = true) => {
  const fields = [
    'category',
    'openingHours',
    'entryFee',
    'tips',
    'accessibility',
    'bestTimeToVisit',
    'keywords',
    'thematicConnections',
    'visitorExperiences'
  ];

  if (includeSummary) {
    fields.unshift('name', 'description');
  }

  if (includeNarration) {
    fields.push(
      'inDepthDescription',
      'historicalContext',
      'culturalSignificance',
      'hiddenGems',
      'subtitle',
      'historicalNarrative'
    );
  }

  const selected = selectTranslationFields(poi, fields);
  const nextStop = selectTranslationFields(poi.nextStop, ['description', 'transportOptions']);
  if (hasTranslationContent(nextStop)) selected.nextStop = nextStop;

  const flexibleContent = selectTranslationFields(poi.flexibleContent, ['title', 'content']);
  if (hasTranslationContent(flexibleContent)) selected.flexibleContent = flexibleContent;

  return selected;
};

const chunkArray = (items, chunkSize) => {
  if (!Array.isArray(items) || chunkSize <= 0) return [];
  const chunks = [];
  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize));
  }
  return chunks;
};

const mergeTranslatedSubset = (original, translated) => {
  if (Array.isArray(original)) {
    if (!Array.isArray(translated)) return original;
    return original.map((value, index) => mergeTranslatedSubset(value, translated[index]));
  }

  if (original && typeof original === 'object') {
    return Object.keys(original).reduce((merged, key) => {
      merged[key] = mergeTranslatedSubset(original[key], translated?.[key]);
      return merged;
    }, {});
  }

  return translated === undefined || translated === null ? original : translated;
};

const parseTranslatedJson = (result) => {
  const jsonString = result.replace(/```(?:json)?\s*/gi, '').trim();
  try {
    return sanitizeTranslatedJson(JSON.parse(jsonString));
  } catch (error) {
    console.error('Failed to parse translated JSON response:', {
      message: error.message,
      previewStart: jsonString.slice(0, 500),
      previewEnd: jsonString.slice(-500)
    });
    throw error;
  }
};

const getFeedbackLanguageCode = (targetLanguage) => (
  String(targetLanguage || 'en').toLowerCase().split(/[-_]/)[0] || 'en'
);

const resolveTranslationInstructions = (customInstructions) => (
  customInstructions || TRANSLATION_INSTRUCTIONS
);

// Service to abstract away provider-specific API calls
export const aiService = {
  async getMediaAssetDataUrl(imageUrl) {
    try {
      const token = await getFirebaseAuthToken();
      const response = await axios.post(
        'https://us-central1-guidesofia-access-control.cloudfunctions.net/backendMediaAssetImageProxy',
        { url: imageUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.data?.dataUrl) throw new Error('Image proxy returned no image data');
      return response.data.dataUrl;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Media image could not be loaded');
    }
  },

  // Generic completion method that routes to the appropriate provider
  async getCompletion(messages, modelId, options = {}) {
    console.log('getCompletion called with modelId:', modelId);
    const provider = getProviderForModel(modelId);
    console.log('Provider detected:', provider);
    const apiKey = API_KEYS[provider];
    const hasApiKey =
      provider === AI_PROVIDERS.OPENAI ||
      provider === AI_PROVIDERS.ANTHROPIC ||
      provider === AI_PROVIDERS.GOOGLE ||
      !!apiKey;
    console.log('API key exists:', hasApiKey);

    if (!hasApiKey) {
      throw new Error(`No API key found for ${provider}. ModelId was: ${modelId}`);
    }
    
    switch(provider) {
      case AI_PROVIDERS.OPENAI:
        return this.getOpenAICompletion(messages, modelId, options);
      case AI_PROVIDERS.ANTHROPIC:
        return this.getAnthropicCompletion(messages, modelId, apiKey, options);
      case AI_PROVIDERS.GOOGLE:
        return this.getGoogleCompletion(messages, modelId, options);
      default:
        throw new Error(`Unsupported AI provider: ${provider}`);
    }
  },

  async parseTranslatedJsonWithRepair(result, modelId) {
    try {
      return parseTranslatedJson(result);
    } catch (initialError) {
      const rawJson = result.replace(/```(?:json)?\s*/gi, '').trim();
      console.warn('Attempting JSON repair pass after translation parse failure');

      const repaired = await this.getCompletion(
        [
          {
            role: 'system',
            content: 'You repair malformed JSON. Return ONLY valid JSON. Preserve the exact structure and text values as much as possible. Do not explain anything.'
          },
          {
            role: 'user',
            content: `Repair this malformed JSON and return only valid JSON:\n\n${rawJson}`
          }
        ],
        modelId,
        {
          maxTokens: Math.max(4096, Math.ceil(rawJson.length / 2))
        }
      );

      try {
        return parseTranslatedJson(repaired);
      } catch (repairError) {
        console.error('JSON repair pass failed:', {
          initialError: initialError.message,
          repairError: repairError.message
        });
        throw repairError;
      }
    }
  },
  
  // OpenAI API call via Firebase proxy so the browser does not need the secret key
  async getOpenAICompletion(messages, modelId, options = {}) {
    try {
      const token = await getFirebaseAuthToken();
      const response = await axios.post(
        'https://us-central1-guidesofia-access-control.cloudfunctions.net/backendItineraryBuilderOpenAIProxy',
        {
          model: modelId,
          messages,
          temperature: options.temperature || 0.7,
          maxTokens: options.maxTokens || undefined,
          reasoningEffort: options.reasoningEffort || undefined
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const content = response.data?.choices?.[0]?.message?.content
        || response.data?.output_text
        || response.data?.response?.output_text;

      if (!content) {
        throw new Error('OpenAI proxy returned an unexpected response format');
      }

      return content;
    } catch (error) {
      console.error('OpenAI API error:', error);
      throw new Error(`OpenAI API error: ${error.response?.data?.error?.message || error.message}`);
    }
  },
  
// Anthropic API call using Firebase proxy
async getAnthropicCompletion(messages, modelId, apiKey, options = {}) {
  try {
    const token = await getFirebaseAuthToken();
    // Extract system message if present
    const systemMessage = messages.find(msg => msg.role === 'system')?.content;
    
    // Format messages in Anthropic's format
    const anthropicMessages = messages
      .filter(msg => msg.role !== 'system')
      .map(msg => ({
        role: msg.role === 'user' ? 'user' : 'assistant',
        content: msg.content
      }));
    
    // Call your Firebase function proxy instead of direct API
    const response = await axios.post(
      'https://us-central1-guidesofia-access-control.cloudfunctions.net/backendItineraryBuilder_claudeapiproxy',
      {
        model: modelId,
        messages: anthropicMessages,
        systemPrompt: systemMessage,
        maxTokens: options.maxTokens || 1024,
        temperature: options.temperature || 0.7
      },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    
    // Return the text from the response
    return response.data.content[0].text;
  } catch (error) {
    console.error('Anthropic API error:', error);
    throw error;
  }
  },

  // Google Gemini API call via Firebase proxy so the browser does not need the secret key
  async getGoogleCompletion(messages, modelId, options = {}) {
    const fallbackModelId = 'gemini-2.0-flash';

    try {
      console.log('Calling Gemini API proxy with model:', modelId);
      const token = await getFirebaseAuthToken();
      const response = await axios.post(
        'https://us-central1-guidesofia-access-control.cloudfunctions.net/backendItineraryBuilderGeminiProxy',
        {
          model: modelId,
          messages,
          temperature: options.temperature || 0.3,
          maxTokens: options.maxTokens || 2048
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          timeout: options.timeoutMs || 240000
        }
      );

      console.log('Gemini API response status:', response.status);
      console.log('Gemini API response data:', JSON.stringify(response.data, null, 2));

      // Check for error responses
      if (response.data.error) {
        console.error('Gemini API returned error:', response.data.error);
        throw new Error(`Gemini API error: ${response.data.error.message}`);
      }

      // Check for empty candidates
      if (!response.data.candidates || response.data.candidates.length === 0) {
        console.error('Gemini API returned no candidates. Full response:', response.data);
        throw new Error('Gemini API returned no candidates (possibly blocked by safety filters)');
      }

      // Check for content
      const candidate = response.data.candidates[0];
      if (!candidate.content || !candidate.content.parts || candidate.content.parts.length === 0) {
        console.error('Gemini API candidate has no content:', candidate);
        if (candidate.finishReason === 'MAX_TOKENS') {
          throw new Error('Gemini response was cut off (MAX_TOKENS). Try increasing maxOutputTokens or shortening the prompt.');
        }
        throw new Error('Gemini API candidate has no content parts');
      }

      // Extract text from Gemini response
      const text = candidate.content.parts[0].text;
      if (!text) {
        console.error('Gemini API part has no text:', candidate.content.parts[0]);
        throw new Error('No text in Gemini response');
      }

      return text;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || error.message || '';
      const shouldFallback =
        modelId !== fallbackModelId &&
        error.response?.status === 404 &&
        /not found|not supported for generateContent/i.test(errorMessage);

      if (shouldFallback) {
        console.warn(`Gemini model ${modelId} is unavailable. Retrying with ${fallbackModelId}.`);
        return this.getGoogleCompletion(messages, fallbackModelId, options);
      }

      console.error('Gemini API error:', error);
      throw new Error(`Gemini API error: ${errorMessage}`);
    }
  },

  // Helper method to clean translation results and remove fluff aggressively
  cleanTranslationResult(text) {
    let cleaned = text.trim();

    // STEP 1: Remove "other variants" sections FIRST (before splitting lines)
    cleaned = cleaned.replace(/(?:outras variantes|other variants|otras variantes|autres variantes|outras opções|other options)[\s\S]*/i, '');
    cleaned = cleaned.replace(/(?:resumo|summary|resumen|contexto|context|impacto|impact|notas?|notes?)[\s\S]*/i, '');

    // STEP 2: Split into lines and process
    let lines = cleaned.split('\n').map(line => line.trim()).filter(line => line.length > 0);

    // STEP 3: Filter out preamble/explanation lines
    const preambleIndicators = [
      /^(?:claro|certainly|of course|bien sûr|seguro|sim|yes)[.,!?\s]/i,
      /(?:tradução|translation|traducción|opções|options|variantes|variants|adequadas?|appropriate)/i,
      /(?:aqui (?:está|ficam?|estão)|here (?:is|are)|voici|voilà)/i,
      /(?:mais (?:comum|literal|idiomática)|most common|more literal)/i,
      /^---+$/,
      /^\*+$/,
      /^#+\s/,  // markdown headers
    ];

    // Filter out lines that are clearly preambles/explanations
    const contentLines = lines.filter(line => {
      // If line contains preamble indicators, skip it
      for (const indicator of preambleIndicators) {
        if (indicator.test(line)) {
          return false;
        }
      }
      // Keep lines that look like actual content
      return true;
    });

    // Get the first content line as the translation
    if (contentLines.length > 0) {
      cleaned = contentLines[0];
    } else if (lines.length > 0) {
      // Fallback to first line if all were filtered out
      cleaned = lines[0];
    }

    // STEP 4: Remove any remaining markdown/formatting
    cleaned = cleaned.replace(/^#+\s+/g, '');  // Remove markdown headers
    cleaned = cleaned.replace(/\*\*/g, '');  // Remove bold
    cleaned = cleaned.replace(/(__)/g, '');  // Remove underline
    cleaned = cleaned.replace(/^\s*[-•*]\s+/g, '');  // Remove bullets
    cleaned = cleaned.replace(/^\s*\d+\.\s+/g, '');  // Remove numbers

    // STEP 5: Final cleanup
    cleaned = cleaned.replace(/\s+/g, ' ').trim();

    return cleaned;
  },

  // Enhanced translate text method with retry logic and validation
  async translateText(text, targetLanguage, modelId = DEFAULT_TRANSLATION_MODEL, customInstructions = TRANSLATION_INSTRUCTIONS) {
    try {
      if (!text || !targetLanguage) {
        throw new Error('Missing required parameters for translation');
      }

      // Skip translation if text is too short or seems to be just punctuation/numbers
      if (text.trim().length < 2 || /^[^\w\s]*$/.test(text.trim()) || /^\d+$/.test(text.trim())) {
        return text;
      }

      const messages = [
        {
          role: 'system',
          content: `${resolveTranslationInstructions(customInstructions)}\n\nTarget language: ${targetLanguage}`
        },
        {
          role: 'user',
          content: text
        }
      ];

      // Get model-specific settings
      const modelSettings = getModelSettings(modelId);
      const optimalTokenLimit = getOptimalTokenLimit(text, modelId);
      
      const options = {
        maxTokens: optimalTokenLimit,
        temperature: modelSettings.temperature
      };

      // Enhanced retry logic with exponential backoff
      let lastError;

      for (let attempt = 0; attempt < modelSettings.retryAttempts; attempt++) {
        try {
          console.log(`Translation attempt ${attempt + 1}/${modelSettings.retryAttempts} for: "${text.substring(0, 50)}..."`);

          let result = await this.getCompletion(messages, modelId, options);

          // Clean up any preambles or fluff that might have been added
          result = sanitizeTranslationOutput(this.cleanTranslationResult(result));

          // Validate the translation result
          validateTranslation(text, result, targetLanguage);

          console.log(`✓ Translation successful on attempt ${attempt + 1}`);
          return result.trim();
          
        } catch (error) {
          lastError = error;
          console.warn(`Translation attempt ${attempt + 1} failed:`, error.message);
          
          // If it's a token limit issue, increase the limit for next attempt
          if (error.message.includes('token') || error.message.includes('MAX_TOKENS')) {
            options.maxTokens = Math.min(options.maxTokens * 2, 8192);
            console.log(`Increasing token limit to ${options.maxTokens} for retry`);
          }
          
          // Don't retry on certain types of errors
          if (error.message.includes('safety') || 
              error.message.includes('API key') ||
              error.message.includes('authentication') ||
              error.message.includes('recitation')) {
            throw error;
          }
          
          // Wait before retry with exponential backoff
          if (attempt < modelSettings.retryAttempts - 1) {
            const delay = 1000 * Math.pow(1.5, attempt);
            console.log(`Waiting ${delay}ms before retry...`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }
      
      // If all retries failed, throw the last error
      throw new Error(`Translation failed after ${modelSettings.retryAttempts} attempts: ${lastError.message}`);
      
    } catch (error) {
      console.error(`Translation failed for text: ${text.substring(0, 50)}...`, error);
      
      // Return original text as fallback to prevent complete failure
      console.warn('Returning original text as fallback');
      return text;
    }
  },

  // JSON-based full guide translation. Send only visitor-facing text and merge it back safely.
  async translateItineraryJSON(itinerary, targetLanguage, modelId = DEFAULT_TRANSLATION_MODEL, customInstructions = TRANSLATION_INSTRUCTIONS, onProgress) {
    try {
      const updateProgress = (message) => {
        if (onProgress) onProgress(message);
      };

      updateProgress('Preparing guide for translation...');

      const sourcePoints = itinerary.points || itinerary.pois || [];
      const modelSettings = getModelSettings(modelId);
      const batchSize = 3;
      const translatableMetadata = buildMetadataTranslationPayload(
        itinerary.metadata,
        itinerary.title,
        itinerary.description
      );
      const translatablePoints = sourcePoints.map(poi => buildPoiTranslationPayload(poi, false));

      updateProgress('Translating guide metadata...');

      const metadataMessages = [
        {
          role: 'system',
          content: `${resolveTranslationInstructions(customInstructions)}\n\nTarget language: ${targetLanguage}\n\nTranslate all text values in the JSON to ${targetLanguage}. Return ONLY valid JSON with translated text values.`
        },
        {
          role: 'user',
          content: JSON.stringify({ metadata: translatableMetadata }, null, 2)
        }
      ];

      const metadataResult = await this.getCompletion(metadataMessages, modelId, {
        maxTokens: modelSettings.maxTokens,
        temperature: modelSettings.temperature
      });

      const translatedMetadataPayload = await this.parseTranslatedJsonWithRepair(metadataResult, modelId);
      const translatedMetadata = mergeTranslatedSubset(
        translatableMetadata,
        translatedMetadataPayload.metadata
      );

      updateProgress(`Translating ${sourcePoints.length} POI summaries in batches...`);

      const translatedPointPayloads = [...translatablePoints];
      const pointBatches = chunkArray(
        translatablePoints.map((point, index) => ({ point, index })),
        batchSize
      );

      for (let batchIndex = 0; batchIndex < pointBatches.length; batchIndex++) {
        const batch = pointBatches[batchIndex];
        const firstPointNumber = batch[0].index + 1;
        const lastPointNumber = batch[batch.length - 1].index + 1;
        updateProgress(`Translating POI summaries ${firstPointNumber}-${lastPointNumber} of ${sourcePoints.length}...`);

        const batchMessages = [
          {
            role: 'system',
            content: `${resolveTranslationInstructions(customInstructions)}\n\nTarget language: ${targetLanguage}\n\nTranslate all text values in the JSON to ${targetLanguage}. Return ONLY valid JSON with translated text values.`
          },
          {
            role: 'user',
            content: JSON.stringify({
              points: batch.map(({ point }) => point)
            }, null, 2)
          }
        ];

        const batchResult = await this.getCompletion(batchMessages, modelId, {
          maxTokens: modelSettings.maxTokens,
          temperature: modelSettings.temperature
        });

        const translatedBatchPayload = await this.parseTranslatedJsonWithRepair(batchResult, modelId);
        const translatedBatchPoints = Array.isArray(translatedBatchPayload.points)
          ? translatedBatchPayload.points
          : [];

        batch.forEach(({ point, index }, offset) => {
          translatedPointPayloads[index] = mergeTranslatedSubset(
            point,
            translatedBatchPoints[offset]
          );
        });
      }

      updateProgress('Applying translations to guide...');

      // Build the translated itinerary
      const translated = {
        ...itinerary,
        metadata: {
          ...itinerary.metadata,
          ...translatedMetadata,
          museum_info: translatedMetadata.museum_info
            ? mergeTranslatedSubset(itinerary.metadata?.museum_info || {}, translatedMetadata.museum_info)
            : itinerary.metadata?.museum_info,
          feedback: translatedMetadata.feedback
            ? {
                ...mergeTranslatedSubset(itinerary.metadata?.feedback || {}, translatedMetadata.feedback),
                language: getFeedbackLanguageCode(targetLanguage)
              }
            : itinerary.metadata?.feedback,
          language: targetLanguage,
          originalLanguage: itinerary.metadata?.language || 'en',
          translatedAt: new Date().toISOString(),
          translationModel: modelId
        },
        title: translatedMetadata.name || itinerary.title,
        description: translatedMetadata.description || itinerary.description,
        points: sourcePoints.map((poi, index) => ({
          ...poi,
          ...mergeTranslatedSubset(
            translatablePoints[index],
            translatedPointPayloads[index]
          ),
          nextStop: translatablePoints[index].nextStop
            ? {
                ...poi.nextStop,
                ...mergeTranslatedSubset(
                  translatablePoints[index].nextStop,
                  translatedPointPayloads[index]?.nextStop
                )
              }
            : poi.nextStop,
          flexibleContent: translatablePoints[index].flexibleContent
            ? {
                ...poi.flexibleContent,
                ...mergeTranslatedSubset(
                  translatablePoints[index].flexibleContent,
                  translatedPointPayloads[index]?.flexibleContent
                )
              }
            : poi.flexibleContent
        })),
        pois: sourcePoints.map((poi, index) => ({
          ...poi,
          ...mergeTranslatedSubset(
            translatablePoints[index],
            translatedPointPayloads[index]
          ),
          nextStop: translatablePoints[index].nextStop
            ? {
                ...poi.nextStop,
                ...mergeTranslatedSubset(
                  translatablePoints[index].nextStop,
                  translatedPointPayloads[index]?.nextStop
                )
              }
            : poi.nextStop,
          flexibleContent: translatablePoints[index].flexibleContent
            ? {
                ...poi.flexibleContent,
                ...mergeTranslatedSubset(
                  translatablePoints[index].flexibleContent,
                  translatedPointPayloads[index]?.flexibleContent
                )
              }
            : poi.flexibleContent
        }))
      };

      updateProgress('Translation complete!');
      console.log(`✅ JSON-based translation complete! Itinerary is now in ${targetLanguage}`);
      return translated;

    } catch (error) {
      console.error('JSON-based translation failed:', error);
      throw new Error(`Itinerary translation failed: ${error.message}`);
    }
  },

  // LEGACY: Text-based itinerary translation (kept for backward compatibility)
  async translateItinerary(itinerary, targetLanguage, modelId = DEFAULT_TRANSLATION_MODEL, customInstructions = TRANSLATION_INSTRUCTIONS, onProgress) {
    try {
      const translated = {...itinerary};
      const totalItems = 4 + itinerary.pois.length; // title + description + subtitle + theme + POIs
      let completed = 0;

      const updateProgress = (message) => {
        if (onProgress) {
          const percentage = Math.round((completed / totalItems) * 100);
          onProgress(`${message} (${percentage}%)`);
        }
      };

      // Add language metadata
      translated.metadata = {
        ...itinerary.metadata,
        language: targetLanguage,
        originalLanguage: itinerary.metadata?.language || 'en',
        translatedAt: new Date().toISOString(),
        translationModel: modelId
      };

      // Translate title
      updateProgress('Translating title...');
      try {
        translated.title = await this.translateText(itinerary.title, targetLanguage, modelId, customInstructions);
        completed++;
        console.log(`✓ Translated title: "${itinerary.title}" → "${translated.title}"`);
      } catch (error) {
        console.error('Failed to translate title, keeping original:', error);
        translated.title = itinerary.title;
        completed++;
      }

      // Translate description
      updateProgress('Translating description...');
      try {
        translated.description = await this.translateText(itinerary.description, targetLanguage, modelId, customInstructions);
        completed++;
        console.log(`✓ Translated description`);
      } catch (error) {
        console.error('Failed to translate description, keeping original:', error);
        translated.description = itinerary.description;
        completed++;
      }

      // Translate subtitle (from metadata)
      updateProgress('Translating subtitle...');
      if (itinerary.metadata?.subtitle) {
        try {
          translated.metadata.subtitle = await this.translateText(
            itinerary.metadata.subtitle,
            targetLanguage,
            modelId,
            customInstructions
          );
          completed++;
          console.log(`✓ Translated subtitle`);
        } catch (error) {
          console.error('Failed to translate subtitle, keeping original:', error);
          completed++;
        }
      } else {
        completed++;
      }

      // Translate theme (from metadata) - usually keep as-is but user can decide
      updateProgress('Processing theme...');
      // Note: Theme is typically NOT translated as it's a category, but included for completeness
      completed++;

      // Translate each POI with individual error handling (serially to avoid rate limiting)
      updateProgress(`Translating ${itinerary.pois.length} points of interest...`);

      translated.pois = [];
      for (let index = 0; index < itinerary.pois.length; index++) {
        const poi = itinerary.pois[index];
        try {
          updateProgress(`Translating POI ${index + 1}/${itinerary.pois.length}: ${poi.name.substring(0, 20)}...`);

          const translatedPoi = {
            ...poi,
            id: poi.id, // Preserve original ID
            address: poi.address, // Keep addresses untranslated
            coordinates: poi.coordinates, // Keep coordinates
          };

          // Translate name and description only (Phase 1)
          try {
            translatedPoi.name = await this.translateText(poi.name, targetLanguage, modelId, customInstructions);
            console.log(`✓ Translated POI name: "${poi.name}" → "${translatedPoi.name}"`);
          } catch (error) {
            console.error(`Failed to translate POI name "${poi.name}", keeping original:`, error);
            translatedPoi.name = poi.name;
          }

          try {
            translatedPoi.description = await this.translateText(poi.description, targetLanguage, modelId, customInstructions);
          } catch (error) {
            console.error(`Failed to translate POI description for "${poi.name}", keeping original:`, error);
            translatedPoi.description = poi.description;
          }

          completed++;
          translated.pois.push(translatedPoi);

        } catch (error) {
          console.error(`Failed to translate POI ${index + 1}, keeping original:`, error);
          completed++;
          translated.pois.push(poi); // Return original POI if translation completely fails
        }
      }

      updateProgress('Translation complete!');
      console.log(`✅ Translation complete! Itinerary is now in ${targetLanguage}`);
      return translated;

    } catch (error) {
      console.error('Failed to translate itinerary:', error);
      throw new Error(`Itinerary translation failed: ${error.message}`);
    }
  },

  // NEW: JSON-based batch translate all POI details (Phase 2 - serially to avoid rate limiting)
  async translateAllPoiDetailsJSON(pois, targetLanguage, modelId = DEFAULT_TRANSLATION_MODEL, customInstructions = TRANSLATION_INSTRUCTIONS, onProgress) {
    try {
      const totalPois = pois.length;
      let completed = 0;

      const updateProgress = (message) => {
        if (onProgress) {
          const percentage = Math.round((completed / totalPois) * 100);
          onProgress(`${message} (${percentage}%)`);
        }
      };

      const translatedPois = [];

      // Translate POIs serially to avoid rate limiting
      for (let index = 0; index < pois.length; index++) {
        const poi = pois[index];
        try {
          updateProgress(`Translating POI ${index + 1}/${totalPois}: ${poi.name}...`);

          const translatedPoi = await this.translatePoiDetailsJSON(
            poi,
            targetLanguage,
            modelId,
            customInstructions
          );

          completed++;
          translatedPois.push(translatedPoi);
        } catch (error) {
          console.error(`Failed to translate POI details for ${poi.name}:`, error);
          completed++;
          translatedPois.push(poi); // Keep original if translation fails
        }
      }

      updateProgress('All POI details translated!');
      console.log(`✅ JSON-based batch translation complete! ${completed}/${totalPois} POIs translated`);
      return translatedPois;

    } catch (error) {
      console.error('Failed to translate POI details in batch:', error);
      throw new Error(`Batch POI translation failed: ${error.message}`);
    }
  },

  // LEGACY: Text-based batch translate all POI details
  async translateAllPoiDetails(pois, targetLanguage, modelId = DEFAULT_TRANSLATION_MODEL, customInstructions = TRANSLATION_INSTRUCTIONS, onProgress) {
    try {
      const totalPois = pois.length;
      let completed = 0;

      const updateProgress = (message) => {
        if (onProgress) {
          const percentage = Math.round((completed / totalPois) * 100);
          onProgress(`${message} (${percentage}%)`);
        }
      };

      // Fields to translate in Phase 2 (in-depth details)
      const detailFields = [
        'inDepthDescription',
        'historicalContext',
        'culturalSignificance',
        'hiddenGems',
        'subtitle',
        'tips',
        'historicalNarrative'
      ];

      const translatedPois = [];

      // Translate POIs serially to avoid rate limiting
      for (let index = 0; index < pois.length; index++) {
        const poi = pois[index];
        try {
          updateProgress(`Translating POI ${index + 1}/${totalPois}: ${poi.name}...`);

          const translatedPoi = { ...poi };

          // Translate all detail fields for this POI
          for (const field of detailFields) {
            if (poi[field] && typeof poi[field] === 'string' && poi[field].trim()) {
              try {
                translatedPoi[field] = await this.translateText(
                  poi[field],
                  targetLanguage,
                  modelId,
                  customInstructions
                );
                console.log(`✓ Translated ${field} for "${poi.name}"`);
              } catch (error) {
                console.error(`Failed to translate ${field} for "${poi.name}", keeping original:`, error);
                translatedPoi[field] = poi[field];
              }
            }
          }

          completed++;
          translatedPois.push(translatedPoi);
        } catch (error) {
          console.error(`Failed to translate POI details for ${poi.name}:`, error);
          completed++;
          translatedPois.push(poi);
        }
      }

      updateProgress('All POI details translated!');
      console.log(`✅ Batch translation complete! ${completed}/${totalPois} POIs translated`);
      return translatedPois;

    } catch (error) {
      console.error('Failed to translate POI details in batch:', error);
      throw new Error(`Batch POI translation failed: ${error.message}`);
    }
  },

  // NEW: JSON-based translation for POI details (Phase 2)
  async translatePoiDetailsJSON(poi, targetLanguage, modelId = DEFAULT_TRANSLATION_MODEL, customInstructions = TRANSLATION_INSTRUCTIONS) {
    try {
      const fieldsToTranslate = buildPoiTranslationPayload(poi, true, false);

      if (Object.keys(fieldsToTranslate).length === 0) {
        console.log(`No detail fields to translate for "${poi.name}"`);
        return poi;
      }

      console.log(`Translating ${Object.keys(fieldsToTranslate).length} detail fields for "${poi.name}" using JSON...`);

      const messages = [
        {
          role: 'system',
          content: `${resolveTranslationInstructions(customInstructions)}\n\nTarget language: ${targetLanguage}\n\nTranslate all text values in the JSON to ${targetLanguage}. Return ONLY valid JSON with translated text values.`
        },
        {
          role: 'user',
          content: JSON.stringify(fieldsToTranslate, null, 2)
        }
      ];

      const modelSettings = getModelSettings(modelId);
      const result = await this.getCompletion(messages, modelId, {
        maxTokens: modelSettings.maxTokens,
        temperature: modelSettings.temperature
      });

      const translatedFields = await this.parseTranslatedJsonWithRepair(result, modelId);

      // Merge translated fields back into POI
      const translatedPoi = {
        ...poi,
        ...mergeTranslatedSubset(fieldsToTranslate, translatedFields),
        nextStop: fieldsToTranslate.nextStop
          ? {
              ...poi.nextStop,
              ...mergeTranslatedSubset(fieldsToTranslate.nextStop, translatedFields.nextStop)
            }
          : poi.nextStop,
        flexibleContent: fieldsToTranslate.flexibleContent
          ? {
              ...poi.flexibleContent,
              ...mergeTranslatedSubset(fieldsToTranslate.flexibleContent, translatedFields.flexibleContent)
            }
          : poi.flexibleContent
      };

      console.log(`✓ Translated ${Object.keys(fieldsToTranslate).length} detail fields for "${poi.name}"`);
      return translatedPoi;

    } catch (error) {
      console.error('JSON-based POI details translation failed:', error);
      throw new Error(`POI details translation failed: ${error.message}`);
    }
  },

  // LEGACY: Text-based POI details translation (kept for backward compatibility)
  async translatePoiDetails(poi, targetLanguage, modelId = DEFAULT_TRANSLATION_MODEL, customInstructions = TRANSLATION_INSTRUCTIONS) {
    try {
      const translatedPoi = { ...poi };

      // Fields to translate in Phase 2 (in-depth details)
      const detailFields = [
        'inDepthDescription',
        'historicalContext',
        'culturalSignificance',
        'hiddenGems',
        'subtitle',
        'tips',
        'historicalNarrative'
      ];

      for (const field of detailFields) {
        if (poi[field] && typeof poi[field] === 'string' && poi[field].trim()) {
          try {
            console.log(`Translating ${field} for "${poi.name}"...`);
            translatedPoi[field] = await this.translateText(
              poi[field],
              targetLanguage,
              modelId,
              customInstructions
            );
            console.log(`✓ Translated ${field}: "${poi[field].substring(0, 30)}..."`);
          } catch (error) {
            console.error(`Failed to translate ${field}, keeping original:`, error);
            translatedPoi[field] = poi[field];
          }
        }
      }

      return translatedPoi;
    } catch (error) {
      console.error('Failed to translate POI details:', error);
      throw new Error(`POI details translation failed: ${error.message}`);
    }
  }
};
